import React from 'react';
import { Phone, Mail, MessageCircle, Linkedin, Building, User, Clock } from 'lucide-react';

interface LeadCardProps {
  lead: any;
  onClick: () => void;
  onDragStart: (e: React.DragEvent, lead: any) => void;
}

const LeadCard: React.FC<LeadCardProps> = ({ lead, onClick, onDragStart }) => {
  console.log('[LeadCard] Received lead prop:', {
    channel: lead.channel,
    last_reply_timestamp: lead.last_reply_timestamp,
    next_fu_datetime: lead.next_fu_datetime,
    company_name: lead.company_name,
    dm_full_name: lead.dm_full_name
  });

  console.log('[LeadCard] Lead data:', lead);
  console.log('[LeadCard] Channel value:', lead.channel);
  console.log('[LeadCard] Last reply timestamp:', lead.last_reply_timestamp);
  console.log('[LeadCard] Next follow up:', lead.next_fu_datetime);

  const getChannelTag = (channel: string) => {
    console.log('[LeadCard] getChannelTag input channel:', channel, 'type:', typeof channel);
    console.log('[LeadCard] Processing channel:', channel);
    
    if (!channel || channel.toString().trim() === '' || channel === 'undefined' || channel === 'null') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
          <MessageCircle size={10} className="mr-1" />
          Unknown
        </span>
      );
    }

    const normalizedChannel = channel.toString().toLowerCase().trim();
    console.log('[LeadCard] Normalized channel:', normalizedChannel);
    
    switch (normalizedChannel) {
      case 'email':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            <Mail size={10} className="mr-1" />
            Email
          </span>
        );
      case 'call':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <Phone size={10} className="mr-1" />
            Call
          </span>
        );
      case 'whatsapp':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <MessageCircle size={10} className="mr-1" />
            WhatsApp
          </span>
        );
      case 'linkedin':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <Linkedin size={10} className="mr-1" />
            LinkedIn
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            <MessageCircle size={10} className="mr-1" />
            {channel}
          </span>
        );
    }
  };

  const getStatusTag = (status: string) => {
    const normalizedStatus = (status || '').toLowerCase().trim();
    
    switch (normalizedStatus) {
      case 'chatting':
      case 'talking':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
            {status}
          </span>
        );
      case 'follow up':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            Follow Up
          </span>
        );
      case 'booked':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-300">
            Booked
          </span>
        );
      case 'open':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            Open
          </span>
        );
      case 'limited':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
            Limited
          </span>
        );
      case 'closed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            Closed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    console.log('[LeadCard] formatTimeAgo input:', timestamp, 'type:', typeof timestamp);
    console.log('[LeadCard] Formatting time ago for:', timestamp);
    
    if (!timestamp || timestamp === 'N/A' || timestamp.toString().trim() === '' || timestamp === 'undefined' || timestamp === 'null') {
      console.log('[LeadCard] formatTimeAgo returning null for invalid timestamp');
      return null;
    }
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        console.log('[LeadCard] Invalid date:', timestamp);
        return null;
      }
      
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
      
      let result;
      if (diffInHours < 1) {
        result = 'Just now';
      } else if (diffInHours < 24) {
        result = `${diffInHours}h ago`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        result = `${diffInDays}d ago`;
      }
      
      console.log('[LeadCard] formatTimeAgo result:', result);
      return result;
    } catch {
      console.log('[LeadCard] Error parsing date:', timestamp);
      return null;
    }
  };

  const formatNextFollowUp = (timestamp: string) => {
    console.log('[LeadCard] formatNextFollowUp input:', timestamp, 'type:', typeof timestamp);
    console.log('[LeadCard] Formatting next follow up for:', timestamp);
    
    if (!timestamp || timestamp === 'N/A' || timestamp.toString().trim() === '' || timestamp === 'undefined' || timestamp === 'null') {
      console.log('[LeadCard] formatNextFollowUp returning null for invalid timestamp');
      return null;
    }
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        console.log('[LeadCard] Invalid follow-up date:', timestamp);
        return null;
      }
      
      const now = new Date();
      const diffInHours = (date.getTime() - now.getTime()) / (1000 * 60 * 60);
      
      let result;
      if (diffInHours < 0) {
        result = 'Overdue';
      } else if (diffInHours < 24) {
        result = `In ${Math.ceil(diffInHours)}h`;
      } else {
        const diffInDays = Math.ceil(diffInHours / 24);
        result = `In ${diffInDays}d`;
      }
      
      console.log('[LeadCard] formatNextFollowUp result:', result);
      return result;
    } catch {
      console.log('[LeadCard] Error parsing follow-up date:', timestamp);
      return null;
    }
  };

  const lastActivity = formatTimeAgo(lead.last_reply_timestamp);
  const nextFollowUp = formatNextFollowUp(lead.next_fu_datetime);

  console.log('[LeadCard] Formatted times:', { lastActivity, nextFollowUp });

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onClick={onClick}
      className="bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-gray-100 transition-colors border border-gray-200 hover:shadow-sm"
    >
      {/* Lead Name */}
      <div className="flex items-center space-x-2 mb-2">
        <User size={14} className="text-gray-500" />
        <span className="font-medium text-gray-900 text-sm">
          {lead.dm_full_name || lead.contact_person || 'Unknown'}
        </span>
      </div>

      {/* Company */}
      <div className="flex items-center space-x-2 mb-2">
        <Building size={14} className="text-gray-500" />
        <span className="text-sm text-gray-700">
          {lead.company_name || 'Unknown Company'}
        </span>
      </div>

      {/* Industry */}
      {(lead.industry || lead.industry_1) && (
        <div className="text-xs text-gray-500 mb-3">
          {lead.industry || lead.industry_1}
        </div>
      )}

      {/* Channel and Status */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          {getChannelTag(lead.channel)}
        </div>
        
        <div className="flex items-center">
          {getStatusTag(lead.status || lead.lead_status)}
        </div>
      </div>

      {/* Activity Information */}
      <div className="mt-2 space-y-1">
        {lastActivity && (
          <div className="text-xs text-gray-500">
            <span className="font-medium">Last reply:</span> {lastActivity}
          </div>
        )}
        {nextFollowUp && (
          <div className="flex items-center space-x-1 text-xs text-blue-600">
            <Clock size={10} />
            <span><span className="font-medium">Follow up:</span> {nextFollowUp}</span>
          </div>
        )}
        {!lastActivity && !nextFollowUp && (
          <div className="text-xs text-gray-400">
            No recent activity
          </div>
        )}
        </div>
    </div>
  );
};

export default LeadCard;